/*
 * Purpose: Classify AI interpretation gaps — infra/setup vs mission knowledge (RAG)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 92
 */
import { REMEDIATION } from '@/lib/remediationRoutes';

export type GapKind = 'infrastructure' | 'connector' | 'knowledge';

/** Infra = vault/API keys; connector = OAuth/services; knowledge = mission-specific docs/RAG. */
export function classifyGap(gap: string): GapKind {
  const lower = gap.toLowerCase();
  if (
    lower.includes('openai') ||
    lower.includes('api key') ||
    lower.includes('vault') ||
    lower.includes('webhook') ||
    lower.includes('redis')
  ) {
    return 'infrastructure';
  }
  if (
    lower.includes('gmail') ||
    lower.includes('linkedin') ||
    lower.includes('oauth') ||
    lower.includes('connector') ||
    (lower.includes('email') && (lower.includes('setup') || lower.includes('inbound') || lower.includes('oauth')))
  ) {
    return 'connector';
  }
  return 'knowledge';
}

export function gapActionLabel(gap: string): string {
  const kind = classifyGap(gap);
  if (kind === 'infrastructure') {
    if (gap.toLowerCase().includes('openai')) return 'Add API key in Setup';
    return 'Open Setup';
  }
  if (kind === 'connector') {
    const lower = gap.toLowerCase();
    if (lower.includes('gmail')) return 'Set up Gmail in Connect';
    if (lower.includes('linkedin')) return 'Set up LinkedIn in Connect';
    return 'Connect service';
  }
  return 'Add to mission library';
}

export function gapRemediationHref(gap: string): string | null {
  const kind = classifyGap(gap);
  const lower = gap.toLowerCase();
  if (kind === 'infrastructure') {
    if (lower.includes('openai')) return REMEDIATION.setupSecrets;
    return REMEDIATION.setup;
  }
  if (kind === 'connector') {
    if (lower.includes('gmail')) return REMEDIATION.setupGoogle;
    if (lower.includes('linkedin')) return REMEDIATION.setup;
    return REMEDIATION.connect;
  }
  return null;
}

export function isKnowledgeGap(gap: string): boolean {
  return classifyGap(gap) === 'knowledge';
}

export function knowledgeGapsFromList(gaps: string[]): string[] {
  return gaps.filter(isKnowledgeGap);
}
